/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountInitialProperties,
  AccountListAll,
  AccountsInterFace,
  APIAccountResponseInterface,
  RequestAccountInterface,
} from '../../interface/account-management.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';
import accountV2Api from '../../utils/api/v2/accounts';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch accounts
export const fetchAllAccounts = createAsyncThunk<
  APIAccountResponseInterface,
  RequestAccountInterface
>('account-management/fetchAllAccounts', async (data: RequestAccountInterface, { dispatch }) => {
  const response = await accountV2Api
    .fetchAccounts(removeEmptyKeys(data))
    .then((response1) => {
      return response1;
    })
    .catch((error) => {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
    });
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      accountType: 'Facility Owner',
      status: roleData?.submission?.status,
      id: roleData?._id,
      phoneNumberCountryCode:
        roleData.countryCode && roleData.phoneNumber
          ? `+${roleData.countryCode}  ${roleData.phoneNumber}`
          : '',
    })),
  };
});

// a thunk to refresh accounts
export const refreshAccounts = createAsyncThunk<
  APIAccountResponseInterface,
  RequestAccountInterface
>('account-management/refreshAccounts', async (data: RequestAccountInterface) => {
  const response = await accountV2Api.fetchAccounts(removeEmptyKeys(data));
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      accountType: 'Facility Owner',
      status: roleData?.submission?.status,
      id: roleData?._id,
      phoneNumberCountryCode:
        roleData.countryCode && roleData.phoneNumber
          ? `${roleData.countryCode}${roleData.phoneNumber}`
          : '',
    })),
  };
});

// a thunk to fetch devices filters
export const fetchAccountManagementFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('account-management/fetchAccountManagementFilters', async (_, { dispatch }) => {
  try {
    const response = await accountV2Api.fetchAccountsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch devices filters
export const fetchAllAccountsId = createAsyncThunk<
  AccountsInterFace[], // Return type
  void // No need for an argument, since we are not using the payload
>('account-management/fetchAllAccountsId', async (_, { dispatch }) => {
  try {
    const response = await accountApi.fetchAllAccounts();
    return response.data.filter((value: AccountsInterFace) => value.accountName);
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchAccountAllList = createAsyncThunk<AccountListAll[], void>(
  'account-management/fetchAccountAllList',
  async (_, { dispatch }) => {
    try {
      const response = await accountV2Api.fetchAccountAllList();
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountInitialProperties = {
  accounts: [],
  accountIds: [],
  accountList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const accountSlice = createSlice({
  name: 'account-management',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateAccountManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableAccountManagementFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The action reducer map builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountInitialProperties>) => {
    builder
      .addCase(fetchAllAccounts.pending, (state: AccountInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllAccounts.fulfilled,
        (state: AccountInitialProperties, action: PayloadAction<APIAccountResponseInterface>) => {
          state.loading = false;
          state.accounts = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllAccounts.rejected, (state: AccountInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(
      refreshAccounts.fulfilled,
      (state: AccountInitialProperties, action: PayloadAction<APIAccountResponseInterface>) => {
        state.accounts = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchAccountManagementFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountManagementFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [
            ...(state.filters || []),
            ...action.payload.filter(
              (filter) => filter.key !== 'revenuePercentageValue-revenuePercentageOperator',
            ),
          ];
        },
      )
      .addCase(fetchAccountManagementFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
    builder
      .addCase(fetchAllAccountsId.pending, () => {})
      .addCase(
        fetchAllAccountsId.fulfilled,
        (state, action: PayloadAction<AccountsInterFace[]>) => {
          state.accountIds = [...action.payload];
        },
      )
      .addCase(fetchAllAccountsId.rejected, () => {});
    builder
      .addCase(fetchAccountAllList.pending, () => {})
      .addCase(fetchAccountAllList.fulfilled, (state, action: PayloadAction<AccountListAll[]>) => {
        state.accountList = action.payload;
      })
      .addCase(fetchAccountAllList.rejected, () => {});
  },
});

// Export the actions
export const {
  updateAccountManagementFilters,
  disableAccountManagementFilterApplied,
  setSortData,
} = accountSlice.actions;

// Export the reducer
export default accountSlice.reducer;
